import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import EAModal from "../../app/components/shared/Modal/EAModal";
import EAButton from "../../app/components/shared/Buttons/EAButton";
import type { ResolvedFacilityAnalytics } from "../../utils/Analitics/analiticsConfigByFacility";
import type { CookieConsent } from "../../utils/cookieConsent/cookieConsentStorage";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onUpdateConsent: (optional: boolean) => void;
  onRejectOptional: () => void;
  resolvedTags: ResolvedFacilityAnalytics;
  privacyPolicyUrl?: string;
  facilityName?: string;
  currentConsent: CookieConsent | null;
}

const CookieSettingsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onUpdateConsent,
  onRejectOptional,
  resolvedTags,
  privacyPolicyUrl,
  facilityName,
  currentConsent,
}) => {
  const { t } = useTranslation();
  const [optionalEnabled, setOptionalEnabled] = useState(currentConsent?.optional ?? false);

  useEffect(() => {
    if (isOpen) {
      setOptionalEnabled(currentConsent?.optional ?? false);
    }
  }, [isOpen, currentConsent?.optional]);

  const activeTools: string[] = [
    resolvedTags.GTM ? "Google Tag Manager" : null,
    resolvedTags.gaId ? "Google Analytics" : null,
    resolvedTags.pixelId ? "Meta Pixel" : null,
  ].filter((t): t is string => t !== null);

  const handleSave = () => {
    onUpdateConsent(optionalEnabled);
  };

  const handleReject = () => {
    onRejectOptional();
  };

  return (
    <EAModal
      isOpen={isOpen}
      onClose={onClose}
      title={t("ustawienia_plikow_cookie")}
      hideButtons
      desktopWidth="600px"
    >
      <div className="cookie-settings-modal">
        <p className="cookie-settings-modal__description">{t("cookie_settings_description")}</p>

        {/* Necessary cookies — always active */}
        <div className="cookie-settings-modal__category">
          <div className="cookie-settings-modal__category-header">
            <span className="cookie-settings-modal__category-title">{t("cookie_necessary_title")}</span>
            <span className="cookie-settings-modal__always-active">{t("cookie_always_active")}</span>
          </div>
          <p className="cookie-settings-modal__category-desc">{t("cookie_necessary_desc")}</p>
        </div>

        {/* Optional cookies — analytics & marketing */}
        <div className="cookie-settings-modal__category">
          <div className="cookie-settings-modal__category-header">
            <span className="cookie-settings-modal__category-title">{t("cookie_optional_title")}</span>
            <label className="cookie-toggle" aria-label={t("cookie_optional_title")}>
              <input
                type="checkbox"
                className="cookie-toggle__input"
                checked={optionalEnabled}
                onChange={(e) => setOptionalEnabled(e.target.checked)}
              />
              <span className="cookie-toggle__track" />
            </label>
          </div>
          <p className="cookie-settings-modal__category-desc">{t("cookie_optional_desc")}</p>
          {activeTools.length > 0 && (
            <ul className="cookie-settings-modal__tools-list">
              {activeTools.map((tool) => (
                <li key={tool}>{tool}</li>
              ))}
            </ul>
          )}
        </div>

        {/* Data controller info */}
        {facilityName && (
          <p className="cookie-settings-modal__description" style={{ marginTop: 0 }}>
            {t("cookie_data_controller")}: <strong>{facilityName}</strong>
          </p>
        )}

        {privacyPolicyUrl && (
          <a
            href={privacyPolicyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="cookie-settings-modal__privacy-link"
          >
            {t("cookie_privacy_policy_link")}
          </a>
        )}

        <div className="cookie-settings-modal__footer">
          <EAButton variant="outlined" title={t("cookie_reject_optional_btn")} onClick={handleReject} />
          <EAButton title={t("cookie_save_settings")} onClick={handleSave} />
        </div>
      </div>
    </EAModal>
  );
};

export default CookieSettingsModal;
